import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, AlertTriangle, Info, ChevronRight, CheckCircle2 } from 'lucide-react';
import { fetchSidebarCounts, subscribe as subscribeSidebarCounts } from '../services/sidebarCountsService';
import { DOMAINS } from './admin/control-center/ccConfig';

const CATEGORY_LABELS = Object.fromEntries(DOMAINS.map((d) => [d.key, d.label]));

const AdminControlCenterBell = ({ bellSize = 20, className = '' }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const [previousCritical, setPreviousCritical] = useState(null);
  const [pulseKey, setPulseKey] = useState(0);
  const [hasAccess, setHasAccess] = useState(true);

  // iter319 — on s'abonne au service partagé /api/admin/sidebar-counts
  // au lieu d'avoir notre propre fetch toutes les 60s.
  useEffect(() => {
    const applySummary = (data) => {
      const cc = data?.control_center;
      if (!cc) return;
      setHasAccess(true);
      if (previousCritical !== null && (cc.critical || 0) > previousCritical) {
        setPulseKey(k => k + 1);
      }
      setPreviousCritical(cc.critical || 0);
      setSummary(cc);
    };
    const unsub = subscribeSidebarCounts(applySummary);
    // Déclenche un premier fetch si le cache est vide (sinon le subscriber l'a déjà livré).
    fetchSidebarCounts().then(d => {
      if (d && d.control_center) applySummary(d);
      else if (d === null) setHasAccess(false);
    });
    return () => { unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fermer sur clic extérieur
  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h, { passive: true });
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('touchstart', h);
    };
  }, [isOpen]);

  const critical = summary?.critical || 0;
  const warning = summary?.warning || 0;
  const info = summary?.info || 0;
  const total = summary?.total || 0;
  const hasCritical = critical > 0;
  const hasAnomalies = total > 0;

  const goToCenter = (tab = 'all') => {
    setIsOpen(false);
    navigate(`/golf/control-center${tab !== 'all' ? `?tab=${tab}` : ''}`);
  };

  // Si l'utilisateur n'a pas accès au centre de contrôle, ne rien afficher
  if (!hasAccess) return null;

  return (
    <div className={`relative ${className}`} ref={panelRef} data-testid="control-center-bell-wrapper">
      <style>{`
        @keyframes acc-vibrate {
          0%, 100% { transform: translateX(0) rotate(0); }
          15% { transform: translateX(-2px) rotate(-6deg); }
          30% { transform: translateX(2px) rotate(6deg); }
          45% { transform: translateX(-2px) rotate(-4deg); }
          60% { transform: translateX(2px) rotate(4deg); }
          75% { transform: translateX(-1px) rotate(-2deg); }
          90% { transform: translateX(1px) rotate(2deg); }
        }
        .acc-shake { animation: acc-vibrate 0.9s ease-in-out infinite; animation-delay: 0.6s; }
        .acc-shake-trigger { animation: acc-vibrate 0.5s ease-in-out 2; }
      `}</style>
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="relative p-2 rounded-lg hover:bg-neutral-100 transition-colors"
        title="Centre de contrôle"
        data-testid="control-center-bell"
      >
        <Shield
          size={bellSize}
          key={pulseKey}
          className={`${hasCritical ? 'text-red-500 acc-shake' : hasAnomalies ? 'text-amber-500' : 'text-neutral-400'}`}
        />
        {total > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-white text-[10px] font-bold rounded-full px-1 ${
              hasCritical ? 'bg-red-500 animate-pulse' : 'bg-amber-500'
            }`}
            data-testid="control-center-badge"
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-neutral-200 z-50 overflow-hidden" data-testid="control-center-panel" style={{ backgroundColor: '#ffffff' }}>
          <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield size={16} className={hasCritical ? 'text-red-500' : 'text-neutral-700'} />
              <span className="font-semibold text-sm text-neutral-900">Centre de contrôle</span>
            </div>
            <button
              onClick={() => goToCenter('all')}
              className="text-xs text-primary hover:underline flex items-center gap-0.5"
              data-testid="control-center-see-all"
            >
              Tout voir <ChevronRight size={12} />
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
            {!summary ? (
              <div className="py-10 text-center text-neutral-400 text-sm">Chargement...</div>
            ) : total === 0 ? (
              <div className="py-10 text-center text-emerald-700">
                <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-2" />
                <p className="text-sm font-medium">Aucune anomalie détectée</p>
                <p className="text-xs text-neutral-500 mt-1">Tout fonctionne normalement.</p>
              </div>
            ) : (
              <div>
                {/* Résumé par sévérité */}
                <div className="grid grid-cols-3 gap-2 px-3 py-3 border-b border-neutral-100">
                  <SeverityPill icon={AlertCircle} count={critical} label="Critiques" color="text-red-600" bg="bg-red-50" />
                  <SeverityPill icon={AlertTriangle} count={warning} label="Attention" color="text-amber-600" bg="bg-amber-50" />
                  <SeverityPill icon={Info} count={info} label="Infos" color="text-blue-600" bg="bg-blue-50" />
                </div>

                {/* Liste par catégorie */}
                <div>
                  {Object.entries(summary.by_category || {}).map(([cat, stats]) => {
                    if (!stats || stats.total === 0) return null;
                    return (
                      <button
                        key={cat}
                        onClick={() => goToCenter(cat)}
                        className="w-full flex items-center justify-between px-4 py-2.5 border-b border-neutral-50 hover:bg-neutral-50 transition-colors text-left"
                        data-testid={`control-cat-${cat}`}
                      >
                        <div>
                          <div className="text-sm font-medium text-neutral-900">
                            {CATEGORY_LABELS[cat] || cat}
                          </div>
                          <div className="text-[11px] text-neutral-500 mt-0.5 flex items-center gap-2">
                            {stats.critical > 0 && <span className="text-red-600 font-medium">{stats.critical} critique(s)</span>}
                            {stats.warning > 0 && <span className="text-amber-600">{stats.warning} attention</span>}
                            {stats.info > 0 && <span className="text-blue-600">{stats.info} info</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold ${
                            stats.critical > 0 ? 'bg-red-500 text-white' : 'bg-neutral-200 text-neutral-700'
                          }`}>
                            {stats.total > 99 ? '99+' : stats.total}
                          </span>
                          <ChevronRight size={14} className="text-neutral-400" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="px-4 py-2.5 border-t border-neutral-100 bg-neutral-50">
            <button
              onClick={() => goToCenter('all')}
              className="w-full text-xs font-semibold text-primary hover:underline"
              data-testid="control-center-open-full"
            >
              Ouvrir le centre de contrôle →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SeverityPill = ({ icon: Icon, count, label, color, bg }) => (
  <div className={`rounded-lg p-2 text-center ${bg}`}>
    <div className="flex items-center justify-center gap-1">
      <Icon size={12} className={color} />
      <span className={`text-lg font-bold ${color}`}>{count}</span>
    </div>
    <div className={`text-[9px] uppercase font-semibold mt-0.5 ${color}`}>{label}</div>
  </div>
);

export default AdminControlCenterBell;
